import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import {
  LayoutDashboard, ShoppingBag, Calendar, ChefHat, Users, BarChart3, Settings,
  LogOut, Menu, Heart, Languages, Truck, Package, Building2, Warehouse,
  DollarSign, MessageSquare, Navigation, Shield, UserCog, UserPlus, UtensilsCrossed, Megaphone, ClipboardList, Inbox, Sparkles, ListChecks, ClipboardCheck
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

const ITEMS = {
  dashboard: { to: "/dashboard", icon: LayoutDashboard, key: "nav.dashboard" },
  orders: { to: "/orders", icon: ShoppingBag, key: "nav.orders" },
  enquiries: { to: "/enquiries", icon: Inbox, key: "nav.enquiries" },
  calendar: { to: "/calendar", icon: Calendar, key: "nav.calendar" },
  marketing: { to: "/marketing", icon: Megaphone, key: "nav.marketing" },
  logistics: { to: "/logistics", icon: Truck, key: "nav.logistics" },
  driver: { to: "/driver", icon: Navigation, key: "nav.driver" },
  kitchen: { to: "/kitchen", icon: ChefHat, key: "nav.kitchen" },
  menu: { to: "/menu", icon: UtensilsCrossed, key: "nav.menu" },
  utensils: { to: "/utensils", icon: ClipboardList, key: "nav.utensils" },
  tasks: { to: "/tasks", icon: ListChecks, key: "nav.tasks" },
  taskmanager: { to: "/task-manager", icon: ClipboardCheck, key: "nav.taskmanager" },
  inventory: { to: "/inventory", icon: Package, key: "nav.inventory" },
  purchasing: { to: "/purchasing", icon: Building2, key: "nav.purchasing" },
  warehouse: { to: "/warehouse", icon: Warehouse, key: "nav.warehouse" },
  staff: { to: "/staff", icon: Users, key: "nav.staff" },
  communications: { to: "/communications", icon: MessageSquare, key: "nav.communications" },
  cost: { to: "/cost-control", icon: DollarSign, key: "nav.cost" },
  reports: { to: "/reports", icon: BarChart3, key: "nav.reports" },
  users: { to: "/users", icon: UserCog, key: "nav.users" },
  signups: { to: "/signup-requests", icon: UserPlus, key: "nav.signups" },
  permissions: { to: "/permissions", icon: Shield, key: "nav.permissions" },
  settings: { to: "/settings", icon: Settings, key: "nav.settings" },
  portal: { to: "/portal", icon: Heart, key: "nav.portal" },
  plan: { to: "/plan-event", icon: Sparkles, key: "nav.plan" },
  corporate: { to: "/corporate", icon: Building2, key: "nav.corporate" },
};

const FALLBACK = {
  admin: ["dashboard", "orders", "enquiries", "calendar", "marketing", "logistics", "driver", "kitchen", "menu", "utensils", "tasks", "taskmanager", "inventory", "purchasing", "warehouse", "staff", "communications", "cost", "reports", "users", "signups", "permissions", "settings"],
  operations: ["dashboard", "orders", "enquiries", "calendar", "marketing", "logistics", "kitchen", "menu", "utensils", "tasks", "taskmanager", "inventory", "purchasing", "warehouse", "staff", "communications", "cost", "reports", "settings"],
  kitchen: ["kitchen", "orders", "menu", "utensils", "tasks", "marketing", "driver", "inventory", "purchasing", "communications", "settings"],
  driver: ["driver", "orders", "calendar", "marketing", "utensils", "tasks", "inventory", "communications", "settings"],
  setup: ["orders", "calendar", "marketing", "driver", "utensils", "tasks", "inventory", "communications", "settings"],
  customer: ["portal", "plan", "corporate", "settings"],
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [permsMap, setPermsMap] = useState(null);

  const loadPerms = async () => {
    try {
      const r = await api.get("/permissions");
      setPermsMap(r.data);
    } catch {
      setPermsMap(FALLBACK);
    }
  };

  useEffect(() => {
    if (user) loadPerms();
    const handler = () => loadPerms();
    window.addEventListener("permissions-updated", handler);
    return () => window.removeEventListener("permissions-updated", handler);
  }, [user]);

  const allRoles = user ? (Array.isArray(user.roles) && user.roles.length ? user.roles : [user.role]) : [];
  // Union permissions across all user roles
  const navKeys = user
    ? Array.from(new Set(allRoles.flatMap((r) => (permsMap && permsMap[r]) ? permsMap[r] : (FALLBACK[r] || []))))
    : [];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-7 pb-5 border-b border-[#1a1a1a]">
        <BrandMark size={36} />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5" data-testid="sidebar-nav">
        {navKeys.map((k) => {
          const it = ITEMS[k];
          if (!it) return null;
          const Icon = it.icon;
          return (
            <NavLink
              key={k}
              to={it.to}
              data-testid={`nav-${k}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              onClick={() => setOpen(false)}
            >
              <Icon className="w-4 h-4" />
              <span>{t(it.key)}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 py-3 border-t border-[#1a1a1a] space-y-2">
        <NotificationBell />
        <button onClick={() => setLang(lang === "en" ? "zh" : "en")} className="nav-link w-full text-left" data-testid="lang-toggle">
          <Languages className="w-4 h-4" />
          <span>{lang === "en" ? "English · 中文" : "中文 · English"}</span>
        </button>
        <div className="px-3 py-2 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a]">
          <div className="text-[10px] tracking-widest uppercase text-zinc-500">{allRoles.join(" · ")}</div>
          <div className="text-sm text-zinc-200 truncate">{user?.name}</div>
        </div>
        <button onClick={handleLogout} className="nav-link w-full text-left" data-testid="logout-btn">
          <LogOut className="w-4 h-4" />
          <span>{t("nav.logout")}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#050505]">
      <aside className="hidden md:flex w-64 shrink-0 border-r border-[#1a1a1a] bg-[#080808]">
        <SidebarContent />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#080808] border-r border-[#1a1a1a]">
            <SidebarContent />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <header className="md:hidden sticky top-0 z-30 glass flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a]">
          <button onClick={() => setOpen(true)} data-testid="menu-open" className="p-2 -ml-2">
            <Menu className="w-5 h-5" />
          </button>
          <div className="font-display tracking-wide">HNC</div>
          <div className="w-8" />
        </header>
        <Outlet />
      </main>
    </div>
  );
}
